// ============================================================================
// api_bandwidth.ts
// Ce que la régie fait passer sur son lien : un débit par tuyau ouvert.
// ============================================================================
//
// Pourquoi ce module existe : la régie ouvre jusqu'à quatre PeerConnection par
// appareil (flux montant, retour, les deux sens de l'intercom), plus la sortie
// WHIP du programme, plus une publication par angle enregistré, plus les flux
// HLS qu'elle tire (playlist, moniteur de retour, Xtream, caméras RTMP). Chacun
// consomme, et personne ne savait combien.
//
// La question n'est pas théorique : le 25/08/2026, sur une prise à trois angles,
// le troisième n'a jamais émis une image. La cause probable est la saturation du
// lien montant, mais rien ne permettait de l'établir — d'où cette mesure, et sa
// trace périodique dans les logs, qui se relit APRÈS coup.
//
// Deux sources de vérité, et deux seulement :
//   - WebRTC : `getStats()` de chaque PeerConnection vivante (inventaire tenu par
//     api_webrtcBuffers). On lit `bytesReceived` / `bytesSent`, qui sont des
//     compteurs CUMULÉS : le débit est leur dérivée entre deux relevés.
//   - HLS : les octets rapportés par hls.js à chaque segment chargé. Le comptage
//     par l'API Resource Timing du navigateur a été écarté — `transferSize` vaut
//     0 sur une origine tierce sans `Timing-Allow-Origin`, et nos flux viennent
//     tous de cast.kleonpro.tv.

import { listTrackedPeerConnections } from './api_webrtcBuffers';
import { logMetric, isMetricsEnabled } from '../debugManagment/remoteLogger';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Ce que la régie DÉCODE d'un flux vidéo entrant — la moitié que personne ne mesurait.
 *
 * Le téléphone sait ce qu'il émet (`[RÉSEAU]`), MediaMTX ne voit passer que des
 * paquets ; seul le navigateur de la régie sait ce qu'il a reçu, combien d'images il a
 * jetées, et avec quelle finesse de compression elles ont été codées. C'est ce
 * dernier chiffre (QP) qui départage « image pixellisée » de « image saccadée ».
 *
 * Tout est mesuré SUR L'INTERVALLE entre deux relevés, pas depuis l'ouverture : une
 * moyenne à vie noierait un défaut survenu en fin de prise.
 */
export interface ReceptionReading {
  /** Définition réellement reçue — celle de l'image décodée, pas de la caméra. */
  def?      : string;
  fps?      : number;
  /**
   * QP moyen des images décodées pendant l'intervalle. Plus il est haut, plus
   * l'image est compressée : ~20 est fin, ~35 commence à se voir, au-delà c'est
   * pixellisé. Absent quand le décodeur ne le publie pas — `?` à l'affichage.
   */
  qp?       : number;
  /** Images jetées par le décodeur pendant l'intervalle. */
  jetees?   : number;
  /** Gels (image arrêtée plus de 150 ms) survenus pendant l'intervalle. */
  gels?     : number;
  /** Paquets perdus et retransmissions demandées pendant l'intervalle. */
  perdus?   : number;
  nack?     : number;
  /** Demandes d'image clé : chacune est un trou que l'encodeur a dû combler. */
  pli?      : number;
  /** `VideoToolbox` (matériel), `OpenH264`, `libvpx`… — dit si le décodage est logiciel. */
  decodeur? : string;
}

/** Les cumuls bruts dont `ReceptionReading` est dérivé, d'un relevé à l'autre. */
interface ReceptionBrute {
  w?: number; h?: number; fps?: number;
  decoded: number; qpSum?: number;
  dropped: number; freezes: number; lost: number; nack: number; pli: number;
  decodeur?: string;
}

export interface PipeReading {
  /** Identifiant stable du tuyau — sert de clé de rendu et de suivi. */
  id      : string;
  /** `webrtc` ou `hls`. */
  family  : 'webrtc' | 'hls';
  /** Nature du tuyau : montant, retour, intercom, whip, whep, hls… */
  kind    : string;
  /** Ce que l'opérateur doit lire : « alex », « programme », « Playlist »… */
  label   : string;
  /** Débit descendant (bits/s) : ce que la régie REÇOIT. */
  inBps   : number;
  /** Débit montant (bits/s) : ce que la régie ÉMET. */
  outBps  : number;
  /** Cumul depuis l'ouverture du tuyau, en octets. */
  inBytes : number;
  outBytes: number;
  /** État de la PeerConnection, quand il y en a une. */
  state?  : string;
  /**
   * Par où passe ce tuyau : `direct` (le pair est en face — un appareil en P2P,
   * l'intercom) ou `mediamtx` (le média transite par le serveur — un appareil en
   * SFU lu en WHEP, le programme publié en WHIP, un angle, un flux HLS).
   *
   * Déduit du `kind`, qui le sait déjà : c'est l'acquéreur qui a nommé la
   * connexion à sa création. La distinction n'était simplement pas lisible pour
   * l'opérateur, qui ne peut pas deviner que « whep » veut dire « par le
   * serveur » — or c'est exactement ce qu'il faut savoir quand une partie du parc
   * est en SFU et l'autre en direct.
   */
  trajet? : 'direct' | 'mediamtx';
  /**
   * Ce qui bride l'encodeur de ce tuyau, dit par le navigateur lui-même :
   * `cpu` (la machine ne suit pas), `bandwidth` (le lien est plein), `none`
   * (rien ne le retient), `other`. C'est LE verdict qui départage les deux
   * causes qu'on confondait jusqu'ici.
   */
  limitation? : string;
  /**
   * Millisecondes de CPU passées à ENCODER par seconde écoulée. 1000 ms/s = un
   * cœur entier occupé par ce seul tuyau.
   */
  encodeMsParSec? : number;
  /** Idem pour le DÉCODAGE d'un flux entrant. */
  decodeMsParSec? : number;
  /**
   * Le tampon de gigue de la RÉGIE sur un flux ENTRANT, en millisecondes par
   * image : le temps qu'elle retient délibérément chaque trame pour lisser le
   * réseau, avant même de la dessiner.
   *
   * C'est le pendant exact de ce que le device mesure sur son retour
   * (`[RETOUR]`, `api_networkQuality`), et le poste le plus lourd de la demi-boucle
   * MONTANTE — la seule que personne ne chiffrait : MediaMTX ne voit que des
   * paquets passer, le téléphone ne connaît que sa propre émission. Seul le
   * navigateur de la régie sait combien de temps il garde une image avant de
   * l'afficher.
   *
   * ⚠️ Valeur INSTANTANÉE, dérivée entre deux relevés. `jitterBufferDelay` est un
   * cumul depuis l'ouverture de la connexion : sa moyenne à vie serait illisible
   * après quelques minutes, un tampon qui double en fin de prise n'y bougerait
   * presque pas. Le device, lui, publie la moyenne à vie — **les deux chiffres ne
   * se comparent pas directement.**
   */
  tamponMs?      : number;
  /** La cible que le navigateur s'est fixée : dit s'il compte encore l'agrandir. */
  tamponCibleMs? : number;
  /**
   * L'aller-retour de CE tuyau. La section `lien` n'en donne qu'un seul, celui de
   * la connexion qui émet le plus — inutile pour un flux entrant, dont le trajet
   * est justement l'autre.
   */
  rttMs?         : number;
  /**
   * Ce que la SOURCE fournit, et ce qui est réellement ÉMIS.
   *
   * Les deux se lisent ensemble, et c'est tout l'intérêt : une source en
   * 1920×1080 émise en 640×360 dit que l'ENCODEUR a réduit, pas la caméra.
   * Constaté le 25/08/2026 sur un angle d'enregistrement — 6,6 Mb/s dépensés
   * pour du 360p.
   */
  sourceDef? : string;
  emisDef?   : string;
  emisFps?   : number;
  /** Ce que la régie décode de ce flux, pour un flux vidéo ENTRANT. */
  reception? : ReceptionReading;
}

/**
 * L'état du lien vers le serveur `cast`, tel que le navigateur le voit.
 *
 * Tout vient des statistiques WebRTC des connexions DÉJÀ ouvertes : aucune sonde,
 * aucun octet dépensé pour mesurer. La paire de candidats retenue porte le temps
 * d'aller-retour et — c'est le chiffre qui manquait le 25/08/2026 quand un angle
 * refusait de démarrer — l'estimation de débit montant DISPONIBLE que le contrôle
 * de congestion du navigateur a calculée.
 */
export interface ServerLinkReading {
  /** `null` tant qu'aucune connexion WebRTC n'est établie. */
  rttMs            : number | null;
  /** Débit montant que le navigateur estime pouvoir utiliser, en bits/s. */
  availableOutBps  : number | null;
  /** `direct` ou `relais` (TURN) — un relais rallonge le chemin et le partage. */
  chemin           : 'direct' | 'relais' | null;
  /** Adresse effectivement jointe, pour lever tout doute sur le chemin pris. */
  adresse          : string | null;
  /** Sur quelle connexion la mesure a été prise (« whip:programme »…). */
  mesureeSur       : string | null;
  /**
   * Ce que CETTE connexion émettait au moment du relevé. Sans lui, l'estimation
   * est illisible : le contrôle de congestion ne découvre la capacité qu'en
   * ESSAYANT. Une connexion au repos reste au plancher de démarrage de Chrome —
   * d'où des valeurs qui sautent de 6 Mb/s à 471 kb/s sans que la ligne ait bougé.
   */
  emisBps          : number | null;
  /**
   * L'estimation vaut-elle quelque chose ? Faux tant que la connexion mesurée
   * n'émet presque rien : le chiffre décrit alors le point de départ de
   * l'estimateur, pas le lien.
   */
  significatif     : boolean;
}

export interface BandwidthReading {
  at       : number;
  pipes    : PipeReading[];
  totalIn  : number;
  totalOut : number;
  /**
   * Ce que la chaîne ELLE-MÊME émet — le seul tuyau `whip:programme`, à
   * l'exclusion des angles enregistrés et des retours P2P vers les
   * téléphones, qui gonflent `totalOut` sans rien dire de la diffusion.
   * `0` si aucune publication de programme n'est ouverte.
   */
  programmeOutBps    : number;
  /** Cumul d'octets envoyés par ce même tuyau depuis son ouverture. */
  programmeOutBytes  : number;
  /** Cumul d'octets envoyés, tous tuyaux confondus (programme, angles, retours, intercom). */
  totalOutBytes      : number;
  lien     : ServerLinkReading;
  /**
   * Coût CPU des médias, en millisecondes par seconde écoulée, tous tuyaux
   * confondus. À rapporter au nombre de cœurs : 4000 ms/s sur une machine à
   * 4 cœurs, c'est la machine entière occupée à encoder et décoder.
   *
   * Il ne s'agit QUE des codecs. Le reste du travail de la régie — composition
   * du canvas, rendu de l'interface — n'y figure pas : il vit sur le fil
   * principal, mesuré séparément par `filPrincipalCharge`.
   */
  cpuMediaMsParSec : number;
  /** Nombre de cœurs annoncé par le navigateur, pour rapporter le chiffre ci-dessus. */
  coeurs           : number | null;
  /**
   * Charge du fil principal, de 0 à 1, mesurée au retard des trames d'animation.
   * C'est lui qui compose le canvas et rend l'interface : saturé, la régie
   * saccade même si les encodeurs, eux, suivent.
   */
  filPrincipalCharge : number;
}

// ─── Comptage HLS ─────────────────────────────────────────────────────────────

const hlsBytes = new Map<string, { total: number; label: string }>();

/**
 * Déclaré par HlsAcquirer à chaque segment chargé. Cumulatif : la dérivée est
 * calculée ici, au relevé, comme pour WebRTC.
 */
export const noteHlsBytes = (sourceId: string, bytes: number, label?: string): void => {
  if (!bytes || bytes <= 0) return;
  const entry = hlsBytes.get(sourceId);
  if (entry) {
    entry.total += bytes;
    if (label) entry.label = label;
  } else {
    // Une ligne par flux, à son premier segment. C'est le seul moyen de savoir
    // APRÈS coup quels flux la mesure a vus — un flux qui manque à l'onglet Débits
    // alors que le navigateur le télécharge se reconnaît à l'absence de cette
    // ligne, et le défaut est alors dans le comptage, pas dans le flux.
    console.warn(`[DÉBITS] 📥 premier segment compté pour « ${label ?? sourceId} » (${sourceId})`);
    hlsBytes.set(sourceId, { total: bytes, label: label ?? sourceId });
  }
};

/** Un flux HLS relâché cesse de compter — sans quoi son dernier débit resterait affiché. */
export const forgetHlsSource = (sourceId: string): void => {
  hlsBytes.delete(sourceId);
};

// ─── Relevé ───────────────────────────────────────────────────────────────────

// ─── Charge du fil principal ──────────────────────────────────────────────────
//
// Aucune API du navigateur ne donne « le CPU de l'onglet ». Ce qu'on peut mesurer,
// c'est le RETARD : on demande une trame d'animation, et on regarde de combien
// elle arrive après l'heure. Un fil libre rend ses trames toutes les ~16,7 ms ; un
// fil saturé les espace. Le rapport entre l'espacement observé et l'espacement
// idéal donne une charge entre 0 et 1.
//
// C'est une mesure grossière et c'est assumé : elle ne dit pas QUI occupe le fil,
// seulement qu'il est occupé. Mais c'est elle qui explique une régie qui saccade
// alors que les encodeurs suivent — la composition du canvas et le rendu de
// l'interface vivent là, pas dans les codecs.

const TRAME_IDEALE_MS = 1000 / 60;
let filPrincipalCharge = 0;
let filPrincipalArme   = false;

const surveillerFilPrincipal = (): void => {
  if (filPrincipalArme || typeof requestAnimationFrame === 'undefined') return;
  filPrincipalArme = true;
  let precedente = performance.now();
  const tour = (maintenant: number) => {
    const ecart = maintenant - precedente;
    precedente  = maintenant;
    // Lissage exponentiel : sans lui, une seule trame en retard ferait bondir
    // l'affichage à 100 % et le chiffre serait illisible.
    const instantane = Math.max(0, Math.min(1, 1 - TRAME_IDEALE_MS / Math.max(ecart, TRAME_IDEALE_MS)));
    filPrincipalCharge = filPrincipalCharge * 0.85 + instantane * 0.15;
    requestAnimationFrame(tour);
  };
  requestAnimationFrame(tour);
};

interface Previous {
  at: number; inBytes: number; outBytes: number;
  encodeSec?: number; decodeSec?: number;
  /** Cumuls du tampon de gigue, nécessaires pour en dériver la valeur courante. */
  jbDelaySec?: number; jbTargetSec?: number; jbEmitted?: number;
  /** Cumuls de la réception vidéo, pour en dériver l'intervalle. */
  reception?: ReceptionBrute;
}

/**
 * Passe des cumuls bruts à ce qui s'est passé PENDANT l'intervalle. Au premier relevé
 * il n'y a pas d'intervalle : les compteurs d'événements restent absents plutôt que
 * de montrer le cumul depuis l'ouverture comme s'il venait d'arriver.
 */
const deriverReception = (brute: ReceptionBrute, avant?: ReceptionBrute): ReceptionReading => {
  const delta = (a: number, b: number | undefined) => Math.max(0, a - (b ?? 0));
  const images = avant ? delta(brute.decoded, avant.decoded) : 0;
  const qp = avant && images > 0 && brute.qpSum != null && avant.qpSum != null
    ? delta(brute.qpSum, avant.qpSum) / images : undefined;
  return {
    def      : brute.w && brute.h ? `${brute.w}×${brute.h}` : undefined,
    fps      : brute.fps,
    qp,
    jetees   : avant ? delta(brute.dropped, avant.dropped) : undefined,
    gels     : avant ? delta(brute.freezes, avant.freezes) : undefined,
    perdus   : avant ? delta(brute.lost,    avant.lost)    : undefined,
    nack     : avant ? delta(brute.nack,    avant.nack)    : undefined,
    pli      : avant ? delta(brute.pli,     avant.pli)     : undefined,
    decodeur : brute.decodeur,
  };
};

const previousPc  = new Map<RTCPeerConnection, Previous>();
const previousHls = new Map<string, Previous>();

/** Débit en bits/s entre deux relevés d'un compteur cumulé d'octets. */
const rate = (previousBytes: number | undefined, previousAt: number | undefined, bytes: number, now: number): number => {
  if (previousBytes === undefined || previousAt === undefined) return 0;
  const seconds = (now - previousAt) / 1000;
  if (seconds <= 0) return 0;
  const delta = bytes - previousBytes;
  return delta > 0 ? (delta * 8) / seconds : 0;
};

const LIEN_VIDE: ServerLinkReading = {
  rttMs: null, availableOutBps: null, chemin: null, adresse: null, mesureeSur: null,
  emisBps: null, significatif: false,
};

/**
 * En dessous, l'estimation ne décrit que le plancher de démarrage de l'estimateur.
 * Chrome part aux alentours de 300 kb/s et ne monte qu'en émettant.
 */
const SEUIL_SIGNIFICATIF_BPS = 300_000;

/**
 * Relève tous les tuyaux. Asynchrone : `getStats()` l'est, et il y a une lecture
 * par PeerConnection — d'où le `Promise.all`, pour que N connexions coûtent un
 * tour et non N.
 */
/**
 * Le trajet d'un tuyau, d'après le nom que son acquéreur lui a donné.
 *
 * `montant` et `retour` sont les deux sens du P2P avec un appareil ; l'intercom
 * est P2P lui aussi, et le reste — WHEP, WHIP, angles, HLS — passe par MediaMTX.
 * Un `kind` inconnu ne reçoit rien plutôt qu'une valeur inventée : mieux vaut une
 * colonne vide qu'un trajet faux.
 */
const trajetDuTuyau = (kind: string): 'direct' | 'mediamtx' | undefined => {
  if (kind === 'montant' || kind === 'retour')  return 'direct';
  if (kind.startsWith('intercom'))              return 'direct';
  if (kind === 'whep' || kind === 'whip')       return 'mediamtx';
  if (kind === 'angle' || kind === 'hls')       return 'mediamtx';
  return undefined;
};

export const sampleBandwidth = async (): Promise<BandwidthReading> => {
  const now   = Date.now();
  const pipes : PipeReading[] = [];

  const tracked = listTrackedPeerConnections();
  const lectures = await Promise.all(tracked.map(async ({ pc, kind, label }) => {
    let inBytes = 0, outBytes = 0;
    let encodeSec = 0, decodeSec = 0;
    let jbDelaySec = 0, jbTargetSec = 0, jbEmitted = 0;
    let rttMs: number | undefined;
    let limitation: string | undefined;
    let emisDef: string | undefined, emisFps: number | undefined;
    let recue: ReceptionBrute | undefined;
    let lien: ServerLinkReading | null = null;
    try {
      const stats = await pc.getStats();
      const rapports = new Map<string, any>();
      stats.forEach(report => {
        rapports.set(report.id, report);
        // `inbound-rtp` et `outbound-rtp` portent les octets de charge utile. On
        // ignore volontairement les rapports `transport` : ils comptent aussi
        // STUN, DTLS et RTCP, ce qui gonfle le chiffre sans rien apprendre.
        if (report.type === 'inbound-rtp') {
          inBytes   += report.bytesReceived  || 0;
          // `totalDecodeTime` est en SECONDES de CPU cumulées.
          decodeSec += report.totalDecodeTime || 0;
          // Le tampon de gigue, vidéo seulement : celui de l'audio se compte en
          // paquets et ne décrit pas le même délai. Ces trois compteurs sont des
          // CUMULS ; ils ne veulent rien dire pris isolément, d'où le report de
          // tout calcul au moment où le relevé précédent est disponible.
          if (report.kind === 'video') {
            jbDelaySec  += report.jitterBufferDelay        || 0;
            jbTargetSec += report.jitterBufferTargetDelay  || 0;
            jbEmitted   += report.jitterBufferEmittedCount || 0;
            // Ce que le décodeur a réellement produit. `qpSum` n'existe pas partout
            // (absent de certains décodeurs matériels) : il reste alors indéfini, et
            // la ligne dit `QP ?` plutôt qu'un zéro qui passerait pour une excellente
            // qualité.
            recue = {
              w: report.frameWidth, h: report.frameHeight, fps: report.framesPerSecond,
              decoded : report.framesDecoded || 0,
              qpSum   : typeof report.qpSum === 'number' ? report.qpSum : undefined,
              dropped : report.framesDropped || 0,
              freezes : report.freezeCount   || 0,
              lost    : report.packetsLost   || 0,
              nack    : report.nackCount     || 0,
              pli     : report.pliCount      || 0,
              decodeur: typeof report.decoderImplementation === 'string' ? report.decoderImplementation : undefined,
            };
          }
        }
        if (report.type === 'outbound-rtp') {
          outBytes  += report.bytesSent      || 0;
          encodeSec += report.totalEncodeTime || 0;
          // Le navigateur nomme lui-même ce qui bride son encodeur. Une seule
          // valeur par tuyau : si deux encodages coexistent, le plus contraint
          // l'emporte — `none` ne doit pas masquer un `cpu`.
          const r = report.qualityLimitationReason;
          if (r && (limitation === undefined || limitation === 'none')) limitation = r;
          // La définition RÉELLEMENT encodée, qui n'est pas celle de la source
          // dès que l'encodeur a jugé bon de réduire.
          if (report.frameWidth && report.frameHeight) {
            emisDef = `${report.frameWidth}×${report.frameHeight}`;
            emisFps = report.framesPerSecond;
          }
        }
      });

      rapports.forEach(report => {
        if (report.type !== 'candidate-pair') return;
        // La paire RETENUE, et elle seule : les autres sont des candidates
        // écartées, dont les chiffres ne décrivent aucun trajet réel.
        if (report.state !== 'succeeded' || report.nominated !== true) return;
        const distant = rapports.get(report.remoteCandidateId);
        // Le même chiffre que `lien`, mais conservé POUR CE TUYAU : sur un flux
        // entrant c'est lui qui dit le trajet, et `lien` ne retiendra que celui
        // de la connexion qui émet le plus.
        if (report.currentRoundTripTime != null) rttMs = report.currentRoundTripTime * 1000;
        lien = {
          rttMs           : report.currentRoundTripTime != null ? report.currentRoundTripTime * 1000 : null,
          availableOutBps : report.availableOutgoingBitrate ?? null,
          chemin          : distant?.candidateType === 'relay' ? 'relais' : 'direct',
          adresse         : distant?.address ?? distant?.ip ?? null,
          mesureeSur      : `${kind}${label ? ':' + label : ''}`,
          // Renseignés plus bas, quand on saura quelle connexion émet le plus.
          emisBps         : null,
          significatif    : false,
        };
      });
    } catch { /* PC fermée entre l'inventaire et la lecture */ }

    // Ce que la SOURCE fournit — la piste elle-même, pas l'encodeur. C'est la seule
    // façon de distinguer « la caméra ne donne que du 360p » de « l'encodeur a
    // réduit ce qu'elle donnait ».
    let sourceDef: string | undefined;
    try {
      for (const sender of pc.getSenders()) {
        if (sender.track?.kind !== 'video') continue;
        const reglages = sender.track.getSettings();
        if (reglages.width && reglages.height) sourceDef = `${reglages.width}×${reglages.height}`;
      }
    } catch { /* piste déjà arrêtée */ }

    // Le cast est nécessaire : l'affectation de `lien` a lieu dans un callback, et
    // l'analyse de flux de TypeScript, qui ne la suit pas, le croirait toujours nul.
    return {
      pc, kind, label, inBytes, outBytes, encodeSec, decodeSec, limitation,
      jbDelaySec, jbTargetSec, jbEmitted, rttMs,
      sourceDef, emisDef, emisFps, recue,
      lien: lien as ServerLinkReading | null,
    };
  }));

  // ── Quelle connexion parle du lien ? ──────────────────────────────────────
  //
  // Celle qui ÉMET LE PLUS. Le contrôle de congestion ne découvre la capacité
  // qu'en essayant : une connexion au repos reste au plancher de démarrage, et
  // c'est ce qui faisait sauter l'affichage de 6 Mb/s à 471 kb/s selon la
  // connexion sur laquelle le relevé tombait. La plus chargée est la seule qui ait
  // réellement sondé le chemin.
  let lien = LIEN_VIDE;
  let meilleurDebit = -1;
  for (const lecture of lectures) {
    if (!lecture.lien || lecture.lien.availableOutBps == null) continue;
    const previous = previousPc.get(lecture.pc);
    const emis = rate(previous?.outBytes, previous?.at, lecture.outBytes, now);
    if (emis <= meilleurDebit) continue;
    meilleurDebit = emis;
    lien = { ...lecture.lien, emisBps: emis, significatif: emis >= SEUIL_SIGNIFICATIF_BPS };
  }

  let cpuMediaMsParSec = 0;
  const idsVus = new Map<string, number>();

  for (const { pc, kind, label, inBytes, outBytes, encodeSec, decodeSec, limitation,
               jbDelaySec, jbTargetSec, jbEmitted, rttMs, sourceDef, emisDef, emisFps, recue } of lectures) {
    const previous = previousPc.get(pc);
    const inBps  = rate(previous?.inBytes,  previous?.at, inBytes,  now);
    const outBps = rate(previous?.outBytes, previous?.at, outBytes, now);

    // Temps CPU consommé depuis le relevé précédent, ramené à la seconde.
    const secondes = previous ? (now - previous.at) / 1000 : 0;
    const encodeMsParSec = previous && secondes > 0
      ? Math.max(0, (encodeSec - (previous.encodeSec ?? 0)) * 1000 / secondes) : undefined;
    const decodeMsParSec = previous && secondes > 0
      ? Math.max(0, (decodeSec - (previous.decodeSec ?? 0)) * 1000 / secondes) : undefined;
    cpuMediaMsParSec += (encodeMsParSec ?? 0) + (decodeMsParSec ?? 0);

    // Le tampon de gigue, ramené à l'image sortie PENDANT cet intervalle.
    // `jitterBufferEmittedCount` compte les images effectivement sorties du
    // tampon : s'il n'a pas bougé, le flux est figé et la moyenne de l'intervalle
    // n'existe pas — on préfère ne rien dire plutôt que de reproduire la dernière
    // valeur connue. Au tout premier relevé il n'y a pas d'intervalle : on montre
    // alors la moyenne depuis l'ouverture, faute de mieux, et elle se corrige
    // d'elle-même au relevé suivant.
    const imagesSorties = jbEmitted - (previous?.jbEmitted ?? 0);
    const parImage = (cumul: number, cumulPrecedent: number | undefined): number | undefined => {
      if (previous) return imagesSorties > 0 ? ((cumul - (cumulPrecedent ?? 0)) / imagesSorties) * 1000 : undefined;
      return jbEmitted > 0 ? (cumul / jbEmitted) * 1000 : undefined;
    };
    const tamponMs      = parImage(jbDelaySec,  previous?.jbDelaySec);
    const tamponCibleMs = parImage(jbTargetSec, previous?.jbTargetSec);

    const reception = recue ? deriverReception(recue, previous?.reception) : undefined;

    previousPc.set(pc, { at: now, inBytes, outBytes, encodeSec, decodeSec, jbDelaySec, jbTargetSec, jbEmitted,
                         reception: recue });

    // L'identifiant sert de clé de rendu : deux tuyaux qui la partagent font
    // paniquer React et l'appareil paraît dupliqué à l'écran. Deux connexions de
    // même nature pour un même appareil peuvent légitimement coexister le temps
    // d'un remplacement — on les distingue plutôt que de parier qu'elles ne se
    // croiseront jamais.
    const baseId = `${kind}:${label}`;
    const rang   = (idsVus.get(baseId) ?? 0) + 1;
    idsVus.set(baseId, rang);

    pipes.push({
      id: rang > 1 ? `${baseId}#${rang}` : baseId, family: 'webrtc', kind, label,
      inBps, outBps, inBytes, outBytes,
      state: pc.connectionState,
      trajet: trajetDuTuyau(kind),
      limitation, encodeMsParSec, decodeMsParSec,
      tamponMs, tamponCibleMs, rttMs,
      sourceDef, emisDef, emisFps, reception,
    });
  }

  // Les PC disparues de l'inventaire ne doivent pas garder leur place en mémoire.
  const vivantes = new Set(tracked.map(t => t.pc));
  previousPc.forEach((_, pc) => { if (!vivantes.has(pc)) previousPc.delete(pc); });

  hlsBytes.forEach((entry, sourceId) => {
    const previous = previousHls.get(sourceId);
    const inBps = rate(previous?.inBytes, previous?.at, entry.total, now);
    previousHls.set(sourceId, { at: now, inBytes: entry.total, outBytes: 0 });
    pipes.push({
      id: `hls:${sourceId}`, family: 'hls', kind: 'hls', label: entry.label,
      inBps, outBps: 0, inBytes: entry.total, outBytes: 0,
      trajet: 'mediamtx',
    });
  });
  previousHls.forEach((_, sourceId) => { if (!hlsBytes.has(sourceId)) previousHls.delete(sourceId); });

  const totalIn  = pipes.reduce((sum, p) => sum + p.inBps,  0);
  const totalOut = pipes.reduce((sum, p) => sum + p.outBps, 0);

  // Le programme est un label CONSTANT (`api_whip.ts`) : une régie ne publie
  // jamais deux fois « programme » à la fois, `find` suffit.
  const programme = pipes.find(p => p.kind === 'whip' && p.label === 'programme');
  const programmeOutBps   = programme?.outBps   ?? 0;
  const programmeOutBytes = programme?.outBytes ?? 0;
  const totalOutBytes     = pipes.reduce((sum, p) => sum + p.outBytes, 0);

  return {
    at: now, pipes, totalIn, totalOut,
    programmeOutBps, programmeOutBytes, totalOutBytes,
    lien,
    cpuMediaMsParSec,
    coeurs: typeof navigator !== 'undefined' ? (navigator.hardwareConcurrency ?? null) : null,
    filPrincipalCharge,
  };
};

// ─── Mise en forme ────────────────────────────────────────────────────────────

export const formatBps = (bps: number): string => {
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mb/s`;
  if (bps >= 1_000)     return `${Math.round(bps / 1_000)} kb/s`;
  return `${Math.round(bps)} b/s`;
};

export const formatBytes = (bytes: number): string => {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(2)} Go`;
  if (bytes >= 1_000_000)     return `${(bytes / 1_000_000).toFixed(1)} Mo`;
  if (bytes >= 1_000)         return `${Math.round(bytes / 1_000)} ko`;
  return `${bytes} o`;
};

/**
 * La ligne qui part dans les logs. Volontairement dense et sur UNE ligne : elle
 * est faite pour être relue en masse après une prise, pas pour être belle.
 *
 * Les tuyaux sous 50 kb/s sont écartés : ils ne pèsent rien et noieraient le reste
 * (une PC d'intercom au repos, un retour à l'arrêt).
 */
export const formatBandwidthLine = (reading: BandwidthReading): string => {
  const hlsIn    = reading.pipes.filter(p => p.family === 'hls').reduce((s, p) => s + p.inBps, 0);
  const webrtcIn = reading.totalIn - hlsIn;

  const detail = reading.pipes
    .filter(p => p.inBps > 50_000 || p.outBps > 50_000)
    .sort((a, b) => (b.inBps + b.outBps) - (a.inBps + a.outBps))
    .slice(0, 12)
    .map(p => {
      const sens = p.outBps > p.inBps ? `↑${formatBps(p.outBps)}` : `↓${formatBps(p.inBps)}`;
      // Le trajet dans la ligne de log aussi : relue après coup, elle doit dire
      // seule quels appareils passaient en direct et lesquels par le serveur.
      const via = p.trajet === 'mediamtx' ? '@srv' : p.trajet === 'direct' ? '@p2p' : '';
      return `${p.kind}:${p.label}${via} ${sens}`;
    })
    .join(' · ');

  const { rttMs, availableOutBps, chemin, emisBps, significatif, mesureeSur } = reading.lien;
  const lien = rttMs == null && availableOutBps == null
    ? 'lien: inconnu (aucune connexion WebRTC)'
    : `lien: rtt ${rttMs != null ? Math.round(rttMs) + ' ms' : '?'}`
      + `, flux montant estimé ${availableOutBps != null ? formatBps(availableOutBps) : '?'}`
      + ` sur ${mesureeSur ?? '?'} qui émet ${emisBps != null ? formatBps(emisBps) : '?'}`
      + `${significatif ? '' : ' [non significatif]'}`
      + `${chemin ? ' (' + chemin + ')' : ''}`;

  const brides = reading.pipes
    .filter(p => p.limitation && p.limitation !== 'none')
    .map(p => `${p.kind}:${p.label}=${p.limitation}`)
    .join(',');
  // Les tampons de gigue des flux ENTRANTS : la demi-boucle montante, en clair.
  // Le trajet vaut ≈ rtt/2, et l'expérience du 27/08 sur le retour dit que c'est
  // le TAMPON qui domine — c'est donc lui qu'il faut lire en premier quand le
  // porteur se plaint d'un décalage. Un écart au-dessus de la cible dit que le
  // navigateur juge le réseau irrégulier et se garde une marge.
  const tampons = reading.pipes
    .filter(p => p.tamponMs != null)
    .sort((a, b) => (b.tamponMs ?? 0) - (a.tamponMs ?? 0))
    .slice(0, 6)
    .map(p => `${p.kind}:${p.label} ${Math.round(p.tamponMs!)} ms`
            + (p.tamponCibleMs != null ? ` (cible ${Math.round(p.tamponCibleMs)})` : '')
            + (p.rttMs != null ? `, rtt ${Math.round(p.rttMs)} ms` : ''))
    .join(' · ');

  // Ce que la régie a DÉCODÉ des flux vidéo entrants. C'est la ligne à lire quand une
  // image paraît pixellisée : le QP dit si l'encodeur distant a trop compressé, les
  // images jetées et les gels disent si c'est le décodage qui n'a pas suivi. Seuls
  // les flux qui portent de la vidéo comptent — un tuyau sans `reception` est un
  // intercom ou un envoi.
  const receptions = reading.pipes
    .filter(p => p.reception && p.inBps > 50_000)
    .sort((a, b) => b.inBps - a.inBps)
    .slice(0, 6)
    .map(p => {
      const r = p.reception!;
      const n = (v?: number) => v == null ? '?' : String(Math.round(v));
      return `${p.kind}:${p.label} ${r.def ?? '?'}@${n(r.fps)}`
           + ` QP ${r.qp != null ? r.qp.toFixed(1) : '?'}`
           + ` | jetées ${n(r.jetees)}, gels ${n(r.gels)}`
           + ` | perdus ${n(r.perdus)} (nack ${n(r.nack)}, pli ${n(r.pli)})`
           + (r.decodeur ? ` | décodeur ${r.decodeur}` : '');
    })
    .join(' · ');

  // Les tuyaux dont l'encodeur a réduit la définition : c'est la première chose à
  // relire quand une prise sort molle alors que rien n'a manqué.
  const reduits = reading.pipes
    .filter(p => p.sourceDef && p.emisDef && p.sourceDef !== p.emisDef)
    .map(p => `${p.kind}:${p.label} ${p.sourceDef}→${p.emisDef}`)
    .join(', ');
  const cpu = `cpu media ${Math.round(reading.cpuMediaMsParSec)} ms/s`
            + `${reading.coeurs ? ` sur ${reading.coeurs} cœurs` : ''}`
            + `, fil principal ${Math.round(reading.filPrincipalCharge * 100)} %`
            + (brides ? `, bridés: ${brides}` : '')
            + (reduits ? ` | RÉDUITS: ${reduits}` : '');

  return `[DÉBITS] ↓ ${formatBps(reading.totalIn)} (webrtc ${formatBps(webrtcIn)}, hls ${formatBps(hlsIn)})`
       + ` ↑ ${formatBps(reading.programmeOutBps)} (cumul ${formatBps(reading.totalOut)})`
       + ` envoyé ${formatBytes(reading.programmeOutBytes)} (cumul ${formatBytes(reading.totalOutBytes)})`
       + ` | ${lien}`
       + ` | ${cpu}`
       + (tampons ? ` | tampons: ${tampons}` : '')
       + (receptions ? ` | réception: ${receptions}` : '')
       + ` | ${reading.pipes.length} tuyau(x)`
       + (detail ? ` | ${detail}` : '');
};

/**
 * Mesure du débit VERS CAST, par le trajet qui compte réellement.
 *
 * `mesurerDebitInternet` juge la ligne face à Internet ; celle-ci juge le chemin
 * que le média emprunte — le poste de régie jusqu'à cast, par le 443 et par
 * HAProxy. Un navigateur ne sait pas parler iperf3 : l'aller-retour HTTP contre
 * notre propre serveur en est l'équivalent le plus fidèle.
 *
 * Elle dépense autant que l'autre, et pour la même raison ne se lance jamais
 * seule.
 */
export const mesurerDebitVersCast = async (
  microserviceUrl: string,
): Promise<{ downBps: number; upBps: number; upBpsServeur: number | null }> => {
  const t0 = performance.now();
  const descente = await fetch(`${microserviceUrl}/link-quality/download?bytes=${OCTETS_DESCENTE}`, { cache: 'no-store' });
  if (!descente.ok) throw new Error(`descente : HTTP ${descente.status}`);
  const recu = await descente.arrayBuffer();
  const secondesDescente = (performance.now() - t0) / 1000;

  const charge = new Uint8Array(OCTETS_MONTEE);
  const t1 = performance.now();
  const montee = await fetch(`${microserviceUrl}/link-quality/upload`, {
    method: 'POST', body: charge, cache: 'no-store',
    headers: { 'Content-Type': 'application/octet-stream' },
  });
  if (!montee.ok) throw new Error(`montée : HTTP ${montee.status}`);
  const secondesMontee = (performance.now() - t1) / 1000;
  // Le serveur mesure la même montée sans compter la fabrication de la charge
  // côté navigateur : c'est la valeur la plus juste des deux.
  const vuServeur = await montee.json().catch(() => null);

  return {
    downBps     : secondesDescente > 0 ? (recu.byteLength * 8) / secondesDescente : 0,
    upBps       : secondesMontee   > 0 ? (OCTETS_MONTEE   * 8) / secondesMontee   : 0,
    upBpsServeur: typeof vuServeur?.debit_bps === 'number' ? vuServeur.debit_bps : null,
  };
};

// ─── Diagnostic ICE : pourquoi tel chemin plutôt qu'un autre ────────────────

export interface IcePairReport {
  connexion         : string;
  local             : string;
  distant           : string;
  adresseDistante   : string;
  etat              : string;
  retenue           : boolean;
  requetesEnvoyees  : number;
  reponsesRecues    : number;
}

/**
 * Détaille les paires de candidats ICE de toutes les connexions vivantes.
 *
 * À quoi ça sert : depuis le 25/08/2026 on sait que 19 sessions sur 19 passent par
 * le relais TURN, jamais en direct. Le relais double le trajet et fait transiter
 * tout le média par la machine qui l'héberge. La question « pourquoi le direct
 * échoue » ne se tranche que du côté client, seul endroit d'où l'on voie ce qui a
 * été TENTÉ.
 *
 * La lecture qui compte : une paire directe avec `requetesEnvoyees > 0` et
 * `reponsesRecues = 0` dit que nos paquets partent et que rien ne revient — le port
 * n'est pas joignable de l'extérieur. Une paire absente dit l'inverse : le candidat
 * n'a même pas été proposé.
 */
export const inspectIcePairs = async (): Promise<IcePairReport[]> => {
  const rapports: IcePairReport[] = [];

  for (const { pc, kind, label } of listTrackedPeerConnections()) {
    try {
      const stats = await pc.getStats();
      const parId = new Map<string, any>();
      stats.forEach(r => parId.set(r.id, r));
      stats.forEach(r => {
        if (r.type !== 'candidate-pair') return;
        const local   = parId.get(r.localCandidateId);
        const distant = parId.get(r.remoteCandidateId);
        rapports.push({
          connexion        : `${kind}${label ? ':' + label : ''}`,
          local            : local?.candidateType ?? '?',
          distant          : distant?.candidateType ?? '?',
          adresseDistante  : `${distant?.address ?? distant?.ip ?? '?'}:${distant?.port ?? '?'}`,
          etat             : r.state ?? '?',
          retenue          : r.nominated === true,
          requetesEnvoyees : r.requestsSent     ?? 0,
          reponsesRecues   : r.responsesReceived ?? 0,
        });
      });
    } catch { /* connexion fermée entre-temps */ }
  }

  return rapports;
};

/** La même chose, poussée dans les journaux distants pour être relue après coup. */
export const logIcePairs = async (): Promise<IcePairReport[]> => {
  const rapports = await inspectIcePairs();
  if (rapports.length === 0) {
    console.warn('[ICE] aucune paire à examiner — aucune connexion WebRTC ouverte');
    return rapports;
  }
  console.warn(`[ICE] ${rapports.length} paire(s) de candidats :`);
  rapports.forEach(r => {
    console.warn(
      `[ICE] ${r.connexion} | ${r.local} → ${r.distant} ${r.adresseDistante}`
      + ` | ${r.etat}${r.retenue ? ' RETENUE' : ''}`
      + ` | envoyées ${r.requetesEnvoyees}, reçues ${r.reponsesRecues}`
    );
  });
  return rapports;
};

// ─── La connexion de la machine, en général ──────────────────────────────────

export interface InternetReading {
  /** Estimation permanente du navigateur (Network Information API), en bits/s. */
  estimationDownBps: number | null;
  /** Temps d'aller-retour estimé par le navigateur, en ms. */
  estimationRttMs  : number | null;
  /** `4g`, `3g`… tel que le navigateur le qualifie. Absent hors Chromium. */
  qualite          : string | null;
}

/**
 * Ce que le navigateur sait DÉJÀ du lien, sans rien mesurer.
 *
 * Chromium l'expose (`navigator.connection`), Firefox et Safari non — d'où les
 * `null`. C'est une estimation glissante fondée sur le trafic récent, pas une
 * mesure : elle vaut comme ordre de grandeur, et elle a le mérite de ne rien
 * coûter, ce qui compte quand on est à l'antenne.
 */
export const readInternetEstimate = (): InternetReading => {
  const c = (navigator as any).connection;
  if (!c) return { estimationDownBps: null, estimationRttMs: null, qualite: null };
  return {
    // `downlink` est en Mb/s.
    estimationDownBps: typeof c.downlink === 'number' ? c.downlink * 1_000_000 : null,
    estimationRttMs  : typeof c.rtt === 'number' ? c.rtt : null,
    qualite          : c.effectiveType ?? null,
  };
};

// Assez gros pour dépasser le démarrage lent de TCP : à 5 Mo sur une ligne
// rapide, la mesure se termine avant que la fenêtre de congestion n'ait fini de
// s'ouvrir, et sous-estime d'autant. Assez petit pour rester supportable en
// pleine émission si quelqu'un s'obstine.
const OCTETS_DESCENTE = 20_000_000;
const OCTETS_MONTEE   = 12_000_000;

/**
 * Mesure réelle, à la demande — jamais automatique.
 *
 * Elle DÉPENSE du lien : 10 Mo en descente, 5 Mo en montée. Lancée pendant une
 * émission, elle prend la bande passante à l'antenne. C'est pour cela qu'elle est
 * derrière un bouton et qu'aucun minuteur ne l'appelle.
 *
 * Le point de mesure est `speed.cloudflare.com`, le même service que celui dont le
 * banc d'essai se sert déjà pour juger de l'extérieur. Rien d'autre n'est envoyé
 * que des octets nuls.
 */
export const mesurerDebitInternet = async (): Promise<{ downBps: number; upBps: number }> => {
  const t0 = performance.now();
  const reponse = await fetch(`https://speed.cloudflare.com/__down?bytes=${OCTETS_DESCENTE}`, { cache: 'no-store' });
  if (!reponse.ok) throw new Error(`descente : HTTP ${reponse.status}`);
  const recu = await reponse.arrayBuffer();
  const secondesDescente = (performance.now() - t0) / 1000;

  const charge = new Uint8Array(OCTETS_MONTEE);
  const t1 = performance.now();
  const envoi = await fetch('https://speed.cloudflare.com/__up', { method: 'POST', body: charge, cache: 'no-store' });
  if (!envoi.ok) throw new Error(`montée : HTTP ${envoi.status}`);
  const secondesMontee = (performance.now() - t1) / 1000;

  return {
    downBps: secondesDescente > 0 ? (recu.byteLength * 8) / secondesDescente : 0,
    upBps  : secondesMontee   > 0 ? (OCTETS_MONTEE   * 8) / secondesMontee   : 0,
  };
};

// ─── Moniteur ─────────────────────────────────────────────────────────────────

type Listener = (reading: BandwidthReading) => void;

const listeners = new Set<Listener>();
let timer      : ReturnType<typeof setInterval> | null = null;
let lastLogAt  = 0;
let lastReading: BandwidthReading | null = null;

export const getLastBandwidthReading = (): BandwidthReading | null => lastReading;

export const subscribeBandwidth = (listener: Listener): (() => void) => {
  listeners.add(listener);
  if (lastReading) listener(lastReading);
  return () => { listeners.delete(listener); };
};

/**
 * Démarre le relevé. Il tourne EN PERMANENCE, que l'onglet Débits soit ouvert ou
 * non : sa raison d'être première est la trace dans les logs, qui doit exister
 * quand on vient la relire — c'est-à-dire toujours après coup.
 *
 * `sampleMs` gouverne l'affichage, `logMs` la trace. Un relevé coûte un
 * `getStats()` par connexion : à 3 s, c'est négligeable devant le rendu de la
 * mosaïque, et assez fin pour voir un angle démarrer.
 */
export const startBandwidthMonitor = (
  { sampleMs = 3_000, logMs = 30_000 }: { sampleMs?: number; logMs?: number } = {},
): (() => void) => {
  if (timer) return () => {};

  surveillerFilPrincipal();

  const tick = async () => {
    let reading: BandwidthReading;
    try {
      reading = await sampleBandwidth();
    } catch (e) {
      console.warn('[DÉBITS] relevé impossible :', e);
      return;
    }
    lastReading = reading;
    listeners.forEach(listener => { try { listener(reading); } catch { /* un abonné ne casse pas les autres */ } });

    // En `warn` et pas en `log` : seuls les warn et les error partent dans les
    // journaux distants, et c'est là que cette ligne doit finir. Rien n'est écrit
    // quand tout est à l'arrêt — une régie ouverte sans flux ne doit pas remplir
    // le journal.
    const now = Date.now();
    if (now - lastLogAt >= logMs && (reading.totalIn > 50_000 || reading.totalOut > 50_000)) {
      lastLogAt = now;
      console.warn(formatBandwidthLine(reading));
    }

    // Canal de métriques : la MÊME ligne, mais dans le fichier de la régie et à
    // chaque relevé, pas toutes les 30 s. Les deux rythmes ne servent pas la même
    // chose — le journal général veut une trace éparse qui ne le noie pas, une
    // courbe veut tous les points. Et ici on n'écarte pas les régies au repos :
    // quand l'opérateur a demandé la mesure, un débit nul est une information.
    if (isMetricsEnabled()) logMetric(formatBandwidthLine(reading));
  };

  timer = setInterval(tick, sampleMs);
  void tick();

  return () => {
    if (timer) { clearInterval(timer); timer = null; }
    previousPc.clear();
    previousHls.clear();
    lastReading = null;
  };
};
